import { exists } from 'helpers/exists'
import { useIsScrollingUp } from 'helpers/hooks/useIsScrolling'
import { useMedia } from 'helpers/hooks/useMedia'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { cdnImageUrl } from 'helpers/image'
import React, { FC } from 'react'
import { IService } from 'services'
import { useService } from 'services/utils/context'
import ProductTileConnected from 'sharedComponents/ProductTileConnected/ProductTileConnected'
import Outfit from 'store/entities/Outfit/Outfit.store'
import styled from 'styled-components'
import { HeadingXXXL } from 'uiComponents/atoms/Text/Headings'
import {
  mediaDesktopAndUp,
  mediaMinL,
  mediaTabletAndDown,
} from 'uiComponents/constants/customMedia'
import { fsXXXL } from 'uiComponents/constants/typography'
import { ProductTileSelector } from 'uiComponents/molecules/ProductTile/ProductTile'
import RecoHeadlineGroup from 'uiComponents/molecules/RecoHeadlineGroup/RecoHeadlineGroup'
import RecoProductGrid from 'uiComponents/molecules/RecoProductGrid/RecoProductGrid'
import EditorialOutfitRecoVerticalImageSlider from './EditorialOutfitRecoVerticalImageSlider'

const PRODUCT_STREAM_HEADER_HEIGHT_WIDE = 68

const CONTENT_HEADER_PADDING_TOP = 60
const CONTENT_HEADER_MARGIN_BOTTOM_MOBILE = 37
const CONTENT_GRID_MARGIN_TOP = 50
const CONTENT_GRID_MARGIN_TOP_MOBILE = 6
const CONTENT_GRID_TILE_MARGIN_BOTTOM = 40

const SPLIT_SECTION_PADDING = 30

// Measured
const CONTENT_SUBTITLE_HEIGHT = 28 + 5
const CONTENT_TITLE_HEIGHT = 51 + 20

interface Props {
  outfitId: string
}

const StyledRecoHeadlineGroup = styled(RecoHeadlineGroup)`
  padding-top: ${CONTENT_HEADER_PADDING_TOP}px;
  @media ${mediaTabletAndDown} {
    margin-bottom: ${CONTENT_HEADER_MARGIN_BOTTOM_MOBILE}px;
  }
`

const StyledRecoProductGrid = styled(RecoProductGrid)`
  width: 100%;
  margin-top: ${CONTENT_GRID_MARGIN_TOP}px;
  @media ${mediaTabletAndDown} {
    margin-top: ${CONTENT_GRID_MARGIN_TOP_MOBILE}px;
  }

  margin-left: 0;

  ${ProductTileSelector} {
    margin: 0 3px ${CONTENT_GRID_TILE_MARGIN_BOTTOM}px;
    width: calc(33.33% - ${12 / 3}px);

    @media ${mediaTabletAndDown} {
      &:nth-child(3n + 1) {
        margin-left: 0;
      }
      &:nth-child(3n + 3) {
        margin-right: 0;
      }
    }

    @media ${mediaDesktopAndUp} {
      width: calc(50% - ${6 / 2}px);

      &:nth-child(2n + 1) {
        margin-left: 0;
      }
      &:nth-child(2n + 2) {
        margin-right: 0;
      }
    }
  }
`

const SplitSection = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  height: 100%;
`

const LeftSide = styled.div<{ scrollingUp: boolean }>`
  position: sticky;
  width: 50%;
  flex-grow: 1;
  margin-right: ${SPLIT_SECTION_PADDING}px;

  top: ${({ scrollingUp }) =>
    scrollingUp ? `${PRODUCT_STREAM_HEADER_HEIGHT_WIDE}px` : 0};

  @media ${mediaMinL} {
    margin-right: ${SPLIT_SECTION_PADDING}px;
  }
`

const RightSide = styled.div`
  width: 50%;
  flex-grow: 1;
  margin-left: ${SPLIT_SECTION_PADDING}px;
  @media ${mediaMinL} {
    margin-left: ${SPLIT_SECTION_PADDING}px;
  }
`

const BrandTitle = styled(HeadingXXXL)`
  margin-bottom: 20px;

  @media ${mediaTabletAndDown} {
    font-size: ${fsXXXL}px;
  }
`

const getOutfitRecoDetails = (
  service: IService,
  outfitId: string,
): {
  outfit: Outfit | null
  availableProductIds: string[]
} => {
  const outfit = service.outfits.getById(outfitId)

  if (!outfit) {
    return { outfit: null, availableProductIds: [] }
  }

  const availableProductIds = service.products.getAvailableIds(
    outfit.productIds,
  )

  if (availableProductIds.length === 0 || outfit.portraitImages.length === 0) {
    return { outfit: null, availableProductIds: [] }
  }

  return { outfit, availableProductIds }
}

const EditorialOutfitReco: FC<Props> = ({ outfitId }) => {
  const { t } = useTranslation()
  const isExtraSmallScreen = useMedia({ [mediaTabletAndDown]: true }, false)
  const service = useService()
  const scrollingUp = useIsScrollingUp()
  const { outfit, availableProductIds } = getOutfitRecoDetails(
    service,
    outfitId,
  )

  if (!exists(outfit)) {
    return null
  }

  const outfitProducts = availableProductIds.map(id => (
    <ProductTileConnected key={id} productId={id} preferredImageType="bust" />
  ))

  const sliderImgSources = outfit.portraitImages.map(hash =>
    cdnImageUrl(hash, { width: 800, height: 800 }),
  )

  const contentSlider = (
    <EditorialOutfitRecoVerticalImageSlider
      scrollable={!isExtraSmallScreen}
      images={sliderImgSources}
    />
  )

  const contentHeadline = (
    <StyledRecoHeadlineGroup
      dataTestId="OutfitRecoHeadlineGroup"
      title={
        <BrandTitle data-testid={'OutfitRecoHeadline'}>
          {t('Passendes Outfit')}
        </BrandTitle>
      }
      subTitle={outfit.idol.name}
    />
  )
  const contentProductGrid = (
    <StyledRecoProductGrid productTiles={outfitProducts} />
  )

  return isExtraSmallScreen ? (
    <>
      {contentHeadline}
      {contentSlider}
      {contentProductGrid}
    </>
  ) : (
    <SplitSection data-testid="StoryOutfitReco">
      <LeftSide scrollingUp={scrollingUp}>{contentSlider}</LeftSide>
      <RightSide>
        {contentHeadline}
        {contentProductGrid}
      </RightSide>
    </SplitSection>
  )
}

interface CalcHeightParams {
  service: IService
  listWidth: number
  outfitId: string
  isExtraSmallScreen?: boolean
}

export const calcHeight = (params: CalcHeightParams) => {
  const { outfit } = getOutfitRecoDetails(params.service, params.outfitId)

  if (!exists(outfit)) {
    return 0
  }

  const sliderHeight = calcSliderHeight(params)
  const contentHeight = calcContentHeight(params, outfit)

  return params.isExtraSmallScreen
    ? sliderHeight + contentHeight
    : Math.max(sliderHeight, contentHeight)
}

function calcSliderHeight({ listWidth, isExtraSmallScreen }: CalcHeightParams) {
  const imgWidth = isExtraSmallScreen
    ? listWidth
    : listWidth / 2 - SPLIT_SECTION_PADDING

  return imgWidth * 1.5
}

function calcContentHeight(params: CalcHeightParams, outfit: Outfit) {
  const headerHeight = calcHeaderHeight(params)
  const productGridHeight = calcProductGridHeight(params, outfit)

  return headerHeight + productGridHeight
}

function calcHeaderHeight({ isExtraSmallScreen }: CalcHeightParams) {
  const headerBottom = isExtraSmallScreen
    ? CONTENT_HEADER_MARGIN_BOTTOM_MOBILE
    : 0

  return (
    CONTENT_HEADER_PADDING_TOP +
    CONTENT_TITLE_HEIGHT +
    CONTENT_SUBTITLE_HEIGHT +
    headerBottom
  )
}

function calcProductGridHeight(
  { service, isExtraSmallScreen, listWidth }: CalcHeightParams,
  outfit: Outfit,
) {
  const heightConstant = CONTENT_GRID_TILE_MARGIN_BOTTOM + 110

  const contentWidth = isExtraSmallScreen
    ? listWidth
    : listWidth / 2 - SPLIT_SECTION_PADDING

  const productTilesPerRow = isExtraSmallScreen ? 3 : 2
  const rowHeight = (contentWidth / productTilesPerRow) * 1.33 + heightConstant

  const availableProductIds = service.products.getAvailableIds(
    outfit.productIds,
  )
  const gridHeight =
    Math.ceil(availableProductIds.length / productTilesPerRow) * rowHeight
  const gridTopMargin = isExtraSmallScreen
    ? CONTENT_GRID_MARGIN_TOP_MOBILE
    : CONTENT_GRID_MARGIN_TOP

  return gridTopMargin + gridHeight
}

export default EditorialOutfitReco
